import { Box, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useServiceRequestsTypeCounts } from "../../hooks/useServiceRequestsTypeCounts";

const serviceRequestsTypes = ["received", "ordered"] as const;
type ServiceRequestType = (typeof serviceRequestsTypes)[number];

const ServicesRequestsType = () => {
  const { t } = useTranslation("goService", { keyPrefix: "myRequestsPage" });
  const [searchParams, setSearchParams] = useSearchParams();
  const { receivedCount, orderedCount, isLoading } = useServiceRequestsTypeCounts();

  const requestType = searchParams.get("request-type") || "received";

  const handleChipClick = (serviceRequestType: ServiceRequestType) => {
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...currentParams, "request-type": serviceRequestType });
  };

  const getCount = (type: ServiceRequestType) => {
    if (isLoading) return null;
    return type === "received" ? receivedCount : orderedCount;
  };

  return (
    <Box sx={styles.container}>
      {serviceRequestsTypes.map((serviceRequestType) => {
        const count = getCount(serviceRequestType);
        return (
          <Chip
            key={serviceRequestType}
            sx={styles.chip(serviceRequestType === requestType)}
            onClick={() => handleChipClick(serviceRequestType)}
            label={
              <Box sx={styles.chipContent}>
                {t(serviceRequestType)}
                {count !== null && count > 0 && <Box sx={styles.badge}>{count}</Box>}
              </Box>
            }
          />
        );
      })}
    </Box>
  );
};

export default ServicesRequestsType;

const styles = {
  container: {
    width: "fit-content",
    display: "flex",
    gap: "4px",
    overflow: "hidden",
    padding: "4px",
    borderRadius: "40px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Gray-50, #F9FAFB)",
  },
  chip: (isActive: boolean) => ({
    padding: "8px 16px",
    color: isActive ? "var(--Gray-700, #344054)" : "var(--Gray-500, #667085)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",

    borderRadius: "40px",
    border: isActive ? "1px solid var(--Gray-300, #D0D5DD)" : "none",
    background: isActive ? "#FFF" : "transparent",
    "&.MuiChip-root": {
      padding: "0",
    },
  }),
  chipContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  badge: {
    display: "flex",
    padding: "2px 8px",
    alignItems: "center",
    borderRadius: "16px",
    border: "1px solid var(--Brand-25, #F6FFF5)",
    background: "var(--Brand-600, #008B07)",
    color: "var(--White, #FFF)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
};
